// Trim a brand mark to its content and read its ground, in the browser.
//
// Partner / organisation logos are uploaded as-is, and most arrive with a lot
// of dead margin baked into the file: Blue Mango's PNG is a 1309×1202 square
// whose wordmark is a band in the middle, Gateway's JPEG is 1600×685 with the
// mark in the middle 40%. Drawn whole into the 90px rail header they came out
// half-size. Nothing server-side can trim them (no image library on the box),
// and the API already serves them with CORS, so the rail does it here: draw
// the decoded image on a canvas, find the bounding box of everything that is
// not the ground, and hand back a cropped PNG data URL plus what the ground
// is — which decides whether the mark needs a light plate under it.
//
// Pure function over an already-loaded <img> (it must have been loaded with
// crossOrigin="anonymous", or getImageData throws a SecurityError — the caller
// catches that and falls back to drawing the file untouched).

const MAX_EDGE = 800; // analysis + output resolution; the rail shows ≤184px, so 2× that is plenty
const MARGIN = 0.04; // breathing room kept around the content, as a fraction of the long edge
const GROUND_TOLERANCE = 60; // |ΔR|+|ΔG|+|ΔB| under which a pixel counts as ground
const MIN_CROP = 0.94; // only crop when at least one axis loses ≥ 6 %

const luminance = (r, g, b) => (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;

/**
 * @param {HTMLImageElement} img loaded image (naturalWidth/Height > 0)
 * @returns {{dataUrl: string, ground: "light"|"dark"|"transparent", plate: boolean, cropped: boolean, width: number, height: number} | null}
 *   `plate` — draw on a light panel: true for a light ground, false for a dark
 *   one, and for a transparent ground true only when the content itself is dark.
 */
export const analyseMark = (img) => {
  const w0 = img.naturalWidth;
  const h0 = img.naturalHeight;
  if (!w0 || !h0) return null;
  const scale = Math.min(1, MAX_EDGE / Math.max(w0, h0));
  const w = Math.max(1, Math.round(w0 * scale));
  const h = Math.max(1, Math.round(h0 * scale));

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, w, h);
  const { data } = ctx.getImageData(0, 0, w, h); // throws SecurityError on a tainted canvas

  // Ground = the average of the one-pixel border ring.
  let gr = 0;
  let gg = 0;
  let gb = 0;
  let ga = 0;
  let n = 0;
  const addPx = (i) => {
    gr += data[i];
    gg += data[i + 1];
    gb += data[i + 2];
    ga += data[i + 3];
    n += 1;
  };
  for (let x = 0; x < w; x += 1) {
    addPx(x * 4);
    addPx(((h - 1) * w + x) * 4);
  }
  for (let y = 1; y < h - 1; y += 1) {
    addPx(y * w * 4);
    addPx((y * w + w - 1) * 4);
  }
  gr /= n;
  gg /= n;
  gb /= n;
  ga /= n;
  const transparent = ga < 40;

  const isGround = (i) => {
    if (transparent) return data[i + 3] < 24;
    return Math.abs(data[i] - gr) + Math.abs(data[i + 1] - gg) + Math.abs(data[i + 2] - gb) < GROUND_TOLERANCE;
  };

  // Bounding box of the content, and its average luminance (for transparent grounds).
  let minX = w;
  let minY = h;
  let maxX = -1;
  let maxY = -1;
  let lumSum = 0;
  let lumN = 0;
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      const i = (y * w + x) * 4;
      if (isGround(i)) continue;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
      lumSum += luminance(data[i], data[i + 1], data[i + 2]);
      lumN += 1;
    }
  }

  const ground = transparent ? "transparent" : luminance(gr, gg, gb) > 0.6 ? "light" : "dark";
  const contentLum = lumN ? lumSum / lumN : 0.5;
  const plate = ground === "light" ? true : ground === "dark" ? false : contentLum < 0.5;

  if (maxX < 0) {
    // Blank image — nothing to trim; hand it back as it is.
    return { dataUrl: canvas.toDataURL("image/png"), ground, plate, cropped: false, width: w, height: h };
  }

  const pad = Math.round(Math.max(w, h) * MARGIN);
  minX = Math.max(0, minX - pad);
  minY = Math.max(0, minY - pad);
  maxX = Math.min(w - 1, maxX + pad);
  maxY = Math.min(h - 1, maxY + pad);
  const cw = maxX - minX + 1;
  const ch = maxY - minY + 1;
  const cropped = cw < w * MIN_CROP || ch < h * MIN_CROP;
  if (!cropped) {
    return { dataUrl: canvas.toDataURL("image/png"), ground, plate, cropped: false, width: w, height: h };
  }

  const out = document.createElement("canvas");
  out.width = cw;
  out.height = ch;
  out.getContext("2d").drawImage(canvas, minX, minY, cw, ch, 0, 0, cw, ch);
  return { dataUrl: out.toDataURL("image/png"), ground, plate, cropped: true, width: cw, height: ch };
};

export default analyseMark;
